import { type ReactNode, useState } from "react";
import { Popover } from "@base-ui/react/popover";
import { MoreVertical } from "lucide-react";

import { cn } from "@/shared/lib/utils";

/**
 * A single menu item rendered inside a {@link CardActionMenu}.
 *
 * Items mirror the visual language of our default buttons (icon on the
 * leading edge, label on the right) but render as menu rows.
 */
export interface CardActionMenuItem {
  /** Stable key for React reconciliation. */
  key: string;
  /** Visible label. */
  label: string;
  /** Optional leading icon (a Lucide icon element). */
  icon?: ReactNode;
  /** Click handler. The menu auto-closes after invocation. */
  onSelect: () => void;
  /** When true, the item is rendered with destructive styling. */
  destructive?: boolean;
  /** When true, the item cannot be selected. */
  disabled?: boolean;
}

interface CardActionMenuProps {
  items: CardActionMenuItem[];
  /** Optional accessible label for the trigger. */
  triggerLabel?: string;
  /** Optional className appended to the trigger button. */
  triggerClassName?: string;
  /** Optional className appended to the popup. */
  contentClassName?: string;
  /** Test id forwarded to the trigger. */
  "data-testid"?: string;
}

/**
 * Card-scoped overflow menu rendered as a square 3-dot button matching the
 * height of our primary CTAs (e.g. `+ Claim`, `Transfer ownership`). The
 * trigger uses a white background with a hairline rule so it sits beside the
 * green CTA without competing visually. The dropdown itself echoes the same
 * button language: icon-on-left, label, hairline border, soft shadow.
 */
export function CardActionMenu({
  items,
  triggerLabel = "More actions",
  triggerClassName,
  contentClassName,
  "data-testid": dataTestId,
}: CardActionMenuProps) {
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        aria-label={triggerLabel}
        title={triggerLabel}
        data-testid={dataTestId}
        className={cn(
          "inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-card)] text-[var(--ink-1)] transition-colors hover:bg-[var(--bg-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--green-bg)]",
          triggerClassName,
        )}
      >
        <MoreVertical className="h-4 w-4" />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Backdrop className="fixed inset-0 z-40" />
        <Popover.Positioner side="bottom" align="end" sideOffset={6} className="isolate z-50">
          <Popover.Popup
            className={cn(
              "min-w-[200px] origin-(--transform-origin) overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-card)] p-1 shadow-[var(--shadow-float)] duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
              contentClassName,
            )}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                // The popup is portalled, so a label-based locator can match a
                // like-named button elsewhere on the record - every card has an
                // "Edit". Keyed here so a test can target this menu's item.
                data-testid={`card-action-${item.key}`}
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  item.onSelect();
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2.5 rounded-[6px] px-2.5 py-2 text-left text-[12.5px] font-normal text-[var(--ink-1)] outline-none transition-colors hover:bg-[var(--bg-subtle)] focus-visible:bg-[var(--bg-subtle)] disabled:cursor-not-allowed disabled:opacity-50",
                  item.destructive &&
                    "text-[var(--status-error)] hover:bg-[color-mix(in_srgb,var(--status-error)_8%,var(--bg-card))]",
                )}
              >
                {item.icon && (
                  <span
                    className={cn(
                      "inline-flex h-4 w-4 shrink-0 items-center justify-center text-[var(--ink-2)] [&_svg]:h-4 [&_svg]:w-4",
                      item.destructive && "text-[var(--status-error)]",
                    )}
                  >
                    {item.icon}
                  </span>
                )}
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
